import * as d3 from 'd3';

export const drawDonutChart = (total: number, element: HTMLDivElement, data: number[], colors: string[]) => {
  // Filter out zero or negative values for better visualization
  const validData = data.map(d => Math.max(0, d));
  const actualTotal = validData.reduce((sum, val) => sum + val, 0);

  const boxSize = 275.5;
  const border = 14;

  // Remove the old svg and tooltip
  d3.select(element).select('svg').remove();
  d3.select(element).select('.donut-tooltip').remove();
  // Ensure parent has relative positioning
  d3.select(element).style('position', 'relative');

  // Create new svg
  const svg = d3
    .select(element)
    .append('svg')
    .attr('preserveAspectRatio', 'xMidYMid meet')
    .attr('height', '100%')
    .attr('width', '100%')
    .attr('viewBox', `0 0 ${boxSize} ${boxSize}`)
    .append('g')
    .attr('transform', `translate(${boxSize / 2}, ${boxSize / 2})`);

  const arcGenerator: any = d3
    .arc()
    .padAngle(0.01)
    .cornerRadius(8)
    .innerRadius(boxSize / 2 - border)
    .outerRadius(boxSize / 2);

  // Set minimum percentage to ensure visibility (e.g., 1%)
  const minPercentage = 0.01; // 1%

  // Create array of objects to keep track of original values
  const dataWithOriginal = validData.map((value, index) => {
    const percentage = value / actualTotal;
    const adjustedValue = percentage > 0 && percentage < minPercentage ? actualTotal * minPercentage : value;

    return {
      original: value,
      adjusted: adjustedValue,
      index,
    };
  });

  const pieGenerator = d3.pie<any>().value(d => d.adjusted);

  const arcs = svg.selectAll('path').data(pieGenerator(dataWithOriginal)).enter();

  const valueLabel = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(total);

  // Create tooltip
  const tooltip = d3
    .select(element)
    .append('div')
    .attr('class', 'donut-tooltip')
    .style('position', 'absolute')
    .style('visibility', 'hidden')
    .style('background-color', 'rgba(0, 0, 0, 0.8)')
    .style('color', 'white')
    .style('border-radius', '4px')
    .style('padding', '8px 12px')
    .style('font-size', '14px')
    .style('pointer-events', 'none')
    .style('z-index', '1000')
    .style('box-shadow', '0 2px 4px rgba(0,0,0,0.2)');

  arcs
    .append('path')
    .attr('d', arcGenerator)
    .style('fill', (_d: any, i: number) => colors[i % validData.length])
    .style('stroke', '#fff')
    .style('stroke-width', '2px')
    .style('cursor', 'pointer')
    .on('mouseover', function (event, d: any) {
      // Use original data for display
      const originalValue = d.data.original;
      const percentage = ((originalValue / actualTotal) * 100).toFixed(2);
      const formattedValue = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(originalValue);

      d3.select(this).style('opacity', 0.8);

      tooltip.style('visibility', 'visible').html(`
          <div><strong>Value:</strong> ${formattedValue}</div>
          <div><strong>Percentage:</strong> ${percentage}%</div>
        `);
    })
    .on('mousemove', function (event) {
      tooltip.style('top', `${event.offsetY - 10}px`).style('left', `${event.offsetX + 10}px`);
    })
    .on('mouseout', function () {
      d3.select(this).style('opacity', 1);
      tooltip.style('visibility', 'hidden');
    });

  svg
    .append('text')
    .attr('dy', '-30px')
    .attr('text-anchor', 'middle')
    .style('font-size', '1.125rem')
    .style('line-height', '1.562rem')
    .style('font-weight', '400')
    .style('fill', '#545454')
    .text('Account Value')
    .transition()
    .duration(700);

  svg
    .append('text')
    .style('text-anchor', 'middle')
    .style('font-size', '28px')
    .style('font-family', 'Source Sans Pro')
    .style('font-style', 'normal')
    .style('font-weight', '700')
    .style('line-height', '36px')
    .style('fill', '#0255A3')
    .attr('class', 'data')
    .text(valueLabel);
};
